import { getCommentElement } from '@/collaboration/collaboration.utils';
import { filter, first, timeout } from 'rxjs/operators';
import isNil from 'lodash/isNil';

const ANNOTATION_FETCH_TIMEOUT = 5000;

const customReactions = {
    '01_THUMBS_UP': {
        emoji: '👍',
    },
    '02_THUMBS_DOWN': {
        emoji: '👎',
    },
    '03_HEART': {
        emoji: '❤️',
    },
    '04_LOOKING': {
        emoji: '👀',
    },
    '05_FIRE': {
        emoji: '🔥',
    },
    '06_RAISED_HANDS': {
        emoji: '🙌',
    },
    '07_HAPPY': {
        emoji: '😄',
    },
    '08_JOY': {
        emoji: '😂',
    },
    '09_HEART_EYES': {
        emoji: '😍',
    },
    '10_CLAPPING': {
        emoji: '👏',
    },
    '11_PLUS': {
        emoji: '➕',
    },
};

export const customStatuses = [
    {
        id: 'open',
        name: 'Open',
        color: 'var(--palette-blue-70)',
        lightColor: 'var(--palette-blue-10)',
        type: 'default',
        svg:
            '<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Frame" clip-path="url(#clip0_920_43450)"><path id="Vector" d="M7.99998 14.6667C11.6819 14.6667 14.6666 11.6819 14.6666 8.00001C14.6666 4.31811 11.6819 1.33334 7.99998 1.33334C4.31808 1.33334 1.33331 4.31811 1.33331 8.00001C1.33331 11.6819 4.31808 14.6667 7.99998 14.6667Z" stroke="var(--palette-blue-80)" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path id="Vector_2" d="M11 9L8 6L5 9" stroke="var(--palette-blue-80)" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_920_43450"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>',
    },
    {
        id: 'inProgress',
        name: 'In progress',
        color: 'var(--palette-yellow-70)',
        lightColor: 'var(--palette-yellow-10)',
        type: 'ongoing',
        svg:
            '<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99998 15.1667C11.6819 15.1667 14.6666 12.1819 14.6666 8.50004C14.6666 4.81814 11.6819 1.83337 7.99998 1.83337C4.31808 1.83337 1.33331 4.81814 1.33331 8.50004C1.33331 12.1819 4.31808 15.1667 7.99998 15.1667Z" stroke="var(--palette-yellow-70)" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 4.5V8.5L10.6667 9.83333" stroke="var(--palette-yellow-70)" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    },
    {
        id: 'resolved',
        name: 'Resolved',
        color: 'var(--palette-green-80)',
        lightColor: 'var(--palette-green-10)',
        type: 'terminal',
        svg:
            '<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99998 15.1667C11.6819 15.1667 14.6666 12.1819 14.6666 8.50004C14.6666 4.81814 11.6819 1.83337 7.99998 1.83337C4.31808 1.83337 1.33331 4.81814 1.33331 8.50004C1.33331 12.1819 4.31808 15.1667 7.99998 15.1667Z" stroke="var(--palette-green-80)" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/><path d="M11 6.83337L7.16665 10.5L5.33331 8.66671" stroke="var(--palette-green-80)" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    },
];

export const customStatusesWithoutResolved = customStatuses.filter((status) => status.id !== 'resolved');

export async function updateContext (annotationId, context) {
    const commentElement = await getCommentElement();

    commentElement.updateContext(annotationId, context, { merge: true });
}

export async function setCustomReactions () {
    const commentElement = await getCommentElement();
    commentElement.setCustomReactions(customReactions);
}

export async function setCustomStatus () {
    const commentElement = await getCommentElement();
    commentElement.setCustomStatus(customStatuses);
}

export async function disableBackspaceDeleteThread () {
    const commentElement = await getCommentElement();
    commentElement.disableDeleteOnBackspace();
}

export async function getCommentThreads () {
    const commentElement = await getCommentElement();

    return commentElement.getAllCommentAnnotations();
}

export async function getCommentAnnotationById ({ annotationId, documentId }) {
    const commentElement = await getCommentElement();

    return commentElement.getCommentAnnotationById({ annotationId, documentId });
}

export async function fetchAnnotation ({ annotationId, documentId, requestTimeout = ANNOTATION_FETCH_TIMEOUT }) {
    const veltObj = await getCommentAnnotationById({ annotationId, documentId });

    // Get the first non-null value and timeout after requestTimeout if no value is emitted
    return veltObj
        .pipe(
            filter((value) => !isNil(value)),
            first(),
            timeout(requestTimeout),
        )
        .toPromise();
}

export async function onCommentAdd () {
    const commentElement = await getCommentElement();

    return commentElement.onCommentAdd();
}

export async function onCommentSelect () {
    const commentElement = await getCommentElement();

    return commentElement.onCommentSelectionChange();
}

export function filterCommentsByLocation (data, commentElement) {
    if (data) {
        const options = {
            grouping: false,
        };

        const actionType = Object.keys(data.actions).find((key) => data.actions[key] === true);
        const isPageAnnotation = actionType === 'page';

        const filteredData = data.systemFilteredData.filter(
            (comment) => !!comment.isPageAnnotation === isPageAnnotation,
        );
        commentElement.setCommentSidebarData([{ annotations: filteredData }], options);
    }
}

export async function setSidebarDefaultCustomActions () {
    const commentElement = await getCommentElement();
    commentElement.enableSidebarCustomActions();
    commentElement.setCommentSidebarDefaultCustomActions({
        widget: true,
        page: false,
    });

    commentElement.onCommentSidebarInit().subscribe((data) => {
        filterCommentsByLocation(data, commentElement);
    });

    commentElement.onCommentSidebarActionButtonClick().subscribe((data) => {
        filterCommentsByLocation(data, commentElement);
    });

    commentElement.onCommentSidebarData().subscribe((data) => {
        filterCommentsByLocation(data, commentElement);
    });
}

export async function setSidebarDefaultEnabledTab (customActions) {
    const commentElement = await getCommentElement();
    commentElement.setCommentSidebarDefaultCustomActions(customActions);
}

export async function scrollToCommentByAnnotationId (annotationId) {
    const commentElement = await getCommentElement();

    commentElement.scrollToCommentByAnnotationId(annotationId);
}

export async function onCommentUpdate () {
    const commentElement = await getCommentElement();

    return commentElement.onCommentUpdate();
}

export async function enableCollapsedComments () {
    const commentElement = await getCommentElement();

    return commentElement.enableCollapsedComments();
}

export async function disableCollapsedComments () {
    const commentElement = await getCommentElement();

    return commentElement.disableCollapsedComments();
}

export async function onCopyLink () {
    const commentElement = await getCommentElement();

    return commentElement.onCopyLink();
}

export async function selectCommentByAnnotationId (annotationId) {
    const commentElement = await getCommentElement();

    commentElement.selectCommentByAnnotationId(annotationId);
}
